import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { IoIosAlert } from "react-icons/io";
import { useSelector } from "react-redux";
import { useUpdateMatchStatus } from "../hooks/useUpdateMatchStatus"

const CustomCheckbox = styled(Checkbox)({
    color: 'rgba(232, 108, 0, 0.5)',
    padding: '4px',
    '&.Mui-checked': {
        color: 'rgba(232, 108, 0, 1)',
        borderColor: 'rgba(232, 108, 0, 1)',
    },
});

const UpdateReviewStatusModal = ({ open, onClose, isProgress, matchId, isCompleted, issueFound }) => {
    const [activeCheckbox, setActiveCheckbox] = useState(null);

    const {
        token,
    } = useSelector((state) => ({
        token: state.auth.token,
    }));

    useEffect(() => {
        isProgress && setActiveCheckbox('inProgress');
        isCompleted && setActiveCheckbox('markAsComplete');
        issueFound && setActiveCheckbox('issueFound');
    }, [isProgress, isCompleted, issueFound]);

    const handleCheckboxChange = (name) => {
        if (name === "inProgress" && isProgress && activeCheckbox === "inProgress") return;
        if (name === "markAsComplete" && isCompleted && activeCheckbox === "markAsComplete") return;
        if (name === "issueFound" && issueFound && activeCheckbox === "issueFound") return;
        setActiveCheckbox((prev) => (prev === name ? null : name));
    };

    const { isUpdateLoading, mutateUpdateMatchStatus } = useUpdateMatchStatus({ onClose })

    const handleUpdate = () => {
        let status;

        if (activeCheckbox === 'inProgress') {
            status = 6;
        } else if (activeCheckbox === 'issueFound') {
            status = 9;
        } else if (activeCheckbox === 'markAsComplete') {
            status = 7;
        }

        if (status) {
            mutateUpdateMatchStatus({ token, match_id: matchId, match_status: status })
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box display={'flex'} alignItems="center" gap={1}>
                    <IoIosAlert style={{ color: 'rgba(144, 152, 161, 0.7)', fontSize: "23px" }} />
                    <Typography sx={{ color: 'var(--text-1)', fontSize: "23px", fontWeight: "600" }}>
                        Update Review Status
                    </Typography>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: 'rgba(144, 152, 161, 0.7)',
                        padding: '4px',
                        '&:hover': {
                            backgroundColor: 'rgba(144, 152, 161, 0.1)',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <Box sx={{ padding: '1rem' }}>
                <DialogContent>
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="center"
                        alignItems="center"
                        gap={4}
                        mt={2}
                    >
                        <FormControlLabel
                            control={
                                <CustomCheckbox
                                    checked={activeCheckbox === 'inProgress'}
                                    onChange={() => handleCheckboxChange('inProgress')}
                                />
                            }
                            label="In Progress"
                        />
                        <FormControlLabel
                            control={
                                <CustomCheckbox
                                    checked={activeCheckbox === 'issueFound'}
                                    onChange={() => handleCheckboxChange('issueFound')}
                                />
                            }
                            label="Issue Found"
                        />
                        <FormControlLabel
                            control={
                                <CustomCheckbox
                                    checked={activeCheckbox === 'markAsComplete'}
                                    onChange={() => handleCheckboxChange('markAsComplete')}
                                />
                            }
                            label="Mark as Complete"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={onClose}
                        sx={{
                            border: "2px solid rgba(144, 152, 161, 0.7)",
                            color: "rgba(144, 152, 161, 0.7)",
                            borderRadius: "6px",
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUpdate}
                        sx={{
                            backgroundColor: 'rgba(232, 108, 0, 1)',
                            '&:hover': {
                                backgroundColor: 'rgba(232, 108, 0, 0.8)',
                            },
                            borderRadius: '6px',
                        }}
                        disabled={!activeCheckbox || isUpdateLoading}
                    >
                        {isUpdateLoading ? 'Updating...' : 'Update'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default UpdateReviewStatusModal;
